import { AccountInfo, InteractionStatus, IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  createContext,
  PropsWithChildren,
  use,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import { LoadingDisplay } from "@/components/shared/LoadingDisplay";
import { useUserMe } from "@/hooks/useUsers";
import { scope } from "@/services/msal";
import { User } from "@/types/user";
import { UserProfileErrorDisplay, AccessDeniedDisplay } from "@/components/shared/ErrorDisplay";

type AuthContextType = {
  accessToken?: string;
  account: AccountInfo;
  currentUser: User;
  setAccessToken: (accessToken: string) => void;
};

const AuthContext = createContext<AuthContextType>({
  accessToken: undefined,
  account: {} as AccountInfo,
  currentUser: {} as User,
  setAccessToken: () => {
    /* stub */
  },
});

// eslint-disable-next-line react-refresh/only-export-components
export const acquireToken = async (
  instance: IPublicClientApplication,
  account: AccountInfo,
  storeAccessToken: (accessToken: string) => void,
  { forceRefresh = false }: { forceRefresh?: boolean } = {},
) => {
  const request = {
    scopes: [scope],
    account,
    forceRefresh,
  };

  try {
    const response = await instance.acquireTokenSilent(request);
    storeAccessToken(response.accessToken);
  } catch (_) {
    await instance.acquireTokenRedirect(request);
  }
};

type AuthContextWrapperProps = PropsWithChildren & {
  accessToken: string;
  account: AccountInfo;
  currentUser: User;
  setAccessToken: (accessToken: string) => void;
};
const AuthContextWrapper = ({
  accessToken,
  account,
  children,
  currentUser,
  setAccessToken,
}: AuthContextWrapperProps) => {
  const contextValue = useMemo(
    () => ({
      accessToken,
      account,
      currentUser,
      setAccessToken,
    }),
    [accessToken, account, currentUser, setAccessToken],
  );

  return <AuthContext value={contextValue}>{children}</AuthContext>;
};

/**
 * AuthContext handles the different authentication states possible within the application.
 *
 * Logging in: InteractionStatus.Login or InteractionStatus.Startup and accounts array is empty
 * Logging out: InteractionStatus.Logout
 *
 * No access token: InteractionStatus.None and we have an account in the msal context
 *
 * Correctly logged in: InteractionStatus.None, we have an account in the msal context and we have a valid access token
 *
 *
 * Token expired but still has an account in msal context:
 *
 * - currentUserError => SWRApiError: "Unauthenticated. Your access token might be expired."
 * - has at least one account in msal context
 *
 */

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { instance, accounts, inProgress: msalStatus } = useMsal();
  const [accessToken, setAccessToken] = useState<string>();

  const {
    isLoading: isLoadingCurrentUser,
    error: currentUserError,
    data: currentUser,
  } = useUserMe(accessToken);

  const getToken = useEffectEvent(
    async ({
      forceRefresh = false,
    }: {
      forceRefresh?: boolean;
    } = {}) => {
      if (accounts.length === 0) {
        // no user, require login
        await instance.loginRedirect();
      } else {
        // user exists, fetch token
        await acquireToken(instance, accounts[0], setAccessToken, { forceRefresh });
      }
    },
  );

  useEffect(() => {
    if (!accessToken && msalStatus === InteractionStatus.None) {
      void getToken();
    }
  }, [accessToken, msalStatus]);

  useEffect(() => {
    if (currentUserError?.statusCode === 401) {
      // force token refresh
      void getToken({ forceRefresh: true });
    }
  }, [currentUserError]);

  if (msalStatus === InteractionStatus.Logout) {
    return (
      <div className="h-screen bg-background">
        <LoadingDisplay text="Logging out..." />
      </div>
    );
  }

  if (
    isLoadingCurrentUser ||
    currentUserError?.statusCode === 401 ||
    msalStatus === InteractionStatus.Login ||
    msalStatus === InteractionStatus.Startup ||
    accounts.length === 0 ||
    !accessToken
  ) {
    return (
      <div className="h-screen bg-background">
        <LoadingDisplay text="Loading user data..." />
      </div>
    );
  }

  const shouldRenderErrorDisplay =
    msalStatus === InteractionStatus.None && (currentUserError || accounts.length === 0);
  if (shouldRenderErrorDisplay || !currentUser) {
    // If we have an access token, are not loading, but no user data and no error,
    // it means the user is authenticated but not found in the database (404 was suppressed by SWR)
    const isUserNotFoundInDatabase =
      currentUserError?.statusCode === 404 ||
      (accessToken && !isLoadingCurrentUser && !currentUser && !currentUserError);

    return (
      <div className="h-screen bg-background">
        {isUserNotFoundInDatabase ? <AccessDeniedDisplay /> : <UserProfileErrorDisplay />}
      </div>
    );
  }

  return (
    <AuthContextWrapper
      accessToken={accessToken}
      currentUser={currentUser}
      account={accounts[0]}
      setAccessToken={setAccessToken}
    >
      {children}
    </AuthContextWrapper>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  return use(AuthContext);
};
